import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { EquipmentTypeSelect } from '@/components/EquipmentTypeSelect';
import { HintTooltip } from '@/components/HintTooltip';
import { CodeTag, StrengthLine, StrengthTag, Tag } from '@/components/matching/matchingTags';
import { TierCard } from '@/components/matching/TierCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useRowClick } from '@/lib/navigation';
import { allEquipmentTypesState, equipmentForModelState, modelSpellingState, modelState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import {
    type BrandRelation,
    type JoinBrand,
    type JoinCandidate,
    type JoinFacts,
    mergeReplacesBrand,
    modelJoinFacts,
    modelJoinStrength,
    modelMergeBlockedReason,
    spellingKeyFor,
} from '@rock/models';
import type { Brand, BrandId, Equipment, EquipmentTypeFamily, EquipmentTypeId, Model, ModelId } from '@rock/types';
import { isCustomId } from '@rock/types';
import { STRONG_IDENTIFIER_MIN_LENGTH, type TypeAffinity } from '@rock/util';
import { Pencil } from 'lucide-react';
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { MergeModelDialog } from './MergeModelDialog';
import { TypeFamilyLine } from './TypeFamilyLine';
import { liveBrandOf, modelJoinBrand, useUnitBrands } from './modelJoin';

/** What each gate of the catalog join decides, in the order a unit meets them. */
export const TYPE_HINT = (
    <>
        <span>
            Only units filed as this type join this model; a unit of a type in its family joins and is flagged for
            review.
        </span>
        <span>
            A model number means nothing across unrelated types: the same model number under another type is another
            model.
        </span>
    </>
);

export const BRAND_HINT = (
    <>
        <span>The owner's brand is the truth: a unit naming any other brand gets its own model, approved or not.</span>
        <span>
            A brand the catalog doesn't have yet is its brand request. The unit joins a model waiting on that request,
            or one with no brand, and otherwise gets a provisional model of its own; resolving the request links it to
            the brand or merges it into that brand's model.
        </span>
        <span>
            A missing brand on either side is no evidence. A unit with no brand joins the best live model (an approved
            brand first, then any other catalog brand, a requested one, none), and its owner is asked only when two or
            more approved brands share the number.
        </span>
    </>
);

/** What a provisional model is: parked on a brand request until an admin resolves it. */
export const PROVISIONAL_HINT = (
    <>
        <span>
            Waiting on a brand an owner typed that the catalog doesn't have yet. A unit naming any other brand stays off
            it; a unit with no brand can still join.
        </span>
        <span>
            Resolving the request links it to that brand, or merges it into the brand's model of the same number and
            type; a resolution with no brand releases it. Its research runs now; its manual hunt waits for the request.
        </span>
    </>
);

export const NUMBER_HINT = (
    <>
        <span>
            Whole model number only, OCR look-alikes included (O/0, I/1, S/5…). A longer model number never joins a
            shorter one.
        </span>
        <StrengthLine strength="strong">
            {STRONG_IDENTIFIER_MIN_LENGTH}+ characters with a letter and a digit. Strength never decides the join — type
            and brand do — it decides what gets flagged.
        </StrengthLine>
        <StrengthLine strength="weak">Everything else, a product name included.</StrengthLine>
    </>
);

/** Tier 1: the type the model is filed under, its family, and the one way to re-file it. */
export const ModelTypePanel: FC<{
    model: Model;
    saving: boolean;
    onSetType: (typeId: EquipmentTypeId) => Promise<boolean>;
}> = ({ model, saving, onSetType }) => {
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const typeTitles = useMemo(() => new Map(types.map((type) => [type.id, type.title])), [types]);
    const custom = isCustomId(model.typeId);
    return (
        <TierCard title="Type" tier={1} hint={TYPE_HINT}>
            <div className="flex flex-wrap items-center gap-2">
                <EquipmentTypeBadge typeId={model.typeId} title={typeTitles.get(model.typeId)} />
                <span className="text-sm">{typeTitles.get(model.typeId) ?? (custom ? 'Custom type' : '')}</span>
                {!custom && (
                    <EquipmentTypeSelect
                        selected={[model.typeId]}
                        preferSpaceId={model.spaceId}
                        disabled={saving}
                        onPick={(ids) => {
                            const next = ids[0];
                            if (next) void onSetType(next);
                        }}
                        trigger={
                            <Button type="button" variant="outline" size="sm" className="ml-auto" disabled={saving}>
                                <Pencil className="h-3.5 w-3.5" />
                                Change type
                            </Button>
                        }
                    />
                )}
            </div>
            <TypeFamilyLine typeId={model.typeId} typeTitles={typeTitles} />
        </TierCard>
    );
};

/** Tier 3: the model number, how much it says on its own, and the look-alike spellings that resolve here. */
export const ModelNumberPanel: FC<{ model: Model }> = ({ model }) => {
    const nameKeyed = model.identity?.keyedBy === 'product-name';
    const lookalikes = model.spellings.filter((spelling) => spelling !== model.modelCanonical);
    return (
        <TierCard title="Model Number" tier={3} hint={NUMBER_HINT}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="break-all font-mono text-sm font-medium">{model.modelCanonical}</span>
                <StrengthTag strength={modelJoinStrength(model)} />
                {nameKeyed && (
                    <HintTooltip hint="Keyed by the product's name because it has no model number on a plate. A name is always a weak identifier.">
                        <Badge variant="outline">product name</Badge>
                    </HintTooltip>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">Look-alikes</span>
                {lookalikes.length === 0 ? (
                    <span className="text-muted-foreground">none observed</span>
                ) : (
                    lookalikes.map((spelling) => <CodeTag key={spelling}>{spelling}</CodeTag>)
                )}
            </div>
        </TierCard>
    );
};

/** The other models under this model's spelling key, each judged against this model as if it were a unit. */
export const SameNumberCard: FC<{
    model: Model;
    brandsById: Map<BrandId, Brand>;
    families: readonly EquipmentTypeFamily[];
}> = ({ model, brandsById, families }) => {
    const key = useMemo(
        () => spellingKeyFor(model.identity?.keyedBy, model.modelCanonical),
        [model.identity?.keyedBy, model.modelCanonical],
    );
    const [projection, projectionLoadable] = useLoadableAtomValue(useMemo(() => modelSpellingState(key), [key]));
    const others = (projection?.entries ?? []).filter((entry) => entry.modelId !== model.id);
    const self = useMemo<JoinCandidate>(
        () => ({ model, brand: modelJoinBrand(model, brandsById) }),
        [model, brandsById],
    );
    const ownerBrands = useOwnerBrands(model.id, brandsById);
    const [mergeInto, setMergeInto] = useState<ModelId>();
    const closeMerge = useCallback((next: boolean) => {
        if (!next) setMergeInto(undefined);
    }, []);
    return (
        <Card className="gap-0 py-4">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">Same Model Number</CardTitle>
                <CardDescription>
                    Other models this model number resolves to, and how each sits beside this one. Two live models of
                    one type are a duplicate to merge when their brands agree, or when no unit names this one's.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                {projectionLoadable.state === 'loading' ? (
                    <span className="text-sm text-muted-foreground">Checking…</span>
                ) : projectionLoadable.state === 'hasError' ? (
                    <span className="text-sm text-destructive">
                        Unable to load the other models under this model number.
                    </span>
                ) : others.length === 0 ? (
                    <span className="text-sm text-muted-foreground">No other model shares this model number.</span>
                ) : (
                    others.map((entry) => (
                        <SameNumberRow
                            key={entry.modelId}
                            modelId={entry.modelId}
                            self={self}
                            brandsById={brandsById}
                            families={families}
                            ownerBrands={ownerBrands}
                            onMerge={model.mergedInto ? undefined : () => setMergeInto(entry.modelId)}
                        />
                    ))
                )}
            </CardContent>
            {mergeInto && (
                <MergeModelDialog
                    open
                    onOpenChange={closeMerge}
                    loser={model}
                    winnerId={mergeInto}
                    brandsById={brandsById}
                />
            )}
        </Card>
    );
};

const SameNumberRow: FC<{
    modelId: ModelId;
    /** The page's model, as the join judges it. */
    self: JoinCandidate;
    brandsById: Map<BrandId, Brand>;
    families: readonly EquipmentTypeFamily[];
    /** The brand each of the page model's units gives on its own label; absent while the units load. */
    ownerBrands: readonly (JoinBrand | undefined)[] | undefined;
    /** Merge the page's model into this one; absent once the page's model is itself a tombstone. */
    onMerge: (() => void) | undefined;
}> = ({ modelId, self, brandsById, families, ownerBrands, onMerge }) => {
    const other = useAsyncAtomValue(useMemo(() => modelState(modelId), [modelId]));
    const onRowClick = useRowClick();
    if (!other) return <span className="font-mono text-xs text-muted-foreground">{modelId}</span>;
    const brand = liveBrandOf(other.brandId, brandsById);
    const candidate: JoinCandidate = { model: other, brand: modelJoinBrand(other, brandsById) };
    const facts = modelJoinFacts(candidate, { typeId: self.model.typeId, brand: self.brand }, families);
    const blocked = other.archivedAt
        ? 'Merge into a live model'
        : mergeBlockedReason(self, candidate, families, ownerBrands);
    return (
        <div className="flex items-stretch gap-2">
            <button
                type="button"
                onClick={onRowClick(`/products/models/${encodeURIComponent(other.id)}`)}
                className="flex flex-1 flex-wrap items-center gap-2 rounded-md border p-2 text-left text-sm hover:bg-muted/50"
            >
                <span className="font-mono font-medium">{other.modelDisplay ?? other.modelCanonical}</span>
                <span className="text-muted-foreground">{brand?.canonicalName ?? 'no brand'}</span>
                <EquipmentTypeBadge typeId={other.typeId} />
                {other.archivedAt && <Badge variant="secondary">Archived</Badge>}
                <span className="ml-auto flex flex-wrap gap-1">
                    <JoinFactTags facts={facts} />
                </span>
            </button>
            <HintTooltip
                className="cursor-default self-center"
                hint={blocked ?? (onMerge ? mergeHint(facts.brand === 'contradicts') : ALREADY_MERGED_HINT)}
            >
                <Button type="button" variant="outline" size="sm" onClick={onMerge} disabled={!onMerge || !!blocked}>
                    Merge into this…
                </Button>
            </HintTooltip>
        </div>
    );
};

const ALREADY_MERGED_HINT = "This page's model is already merged.";

/** The merge gate, with a brand that would give way held until the units' own brands have loaded. */
function mergeBlockedReason(
    self: JoinCandidate,
    candidate: JoinCandidate,
    families: readonly EquipmentTypeFamily[],
    ownerBrands: readonly (JoinBrand | undefined)[] | undefined,
): string | undefined {
    const reason = modelMergeBlockedReason(self, candidate, families, ownerBrands ?? []);
    if (reason || ownerBrands || !mergeReplacesBrand(self, candidate)) return reason;
    return "Checking the brands on this model's units…";
}

function mergeHint(replacesBrand: boolean): string {
    const moves =
        "Moves every unit, spelling, known-as name and manual link from this page's model into this one, and leaves this page's model as a pointer to it.";
    return replacesBrand ? `${moves} No unit names this page's brand, so this model's brand stands.` : moves;
}

/**
 * The brand each of a model's units gives on its own label, for the merge gate; a deleted unit never
 * refreshes, so it names nothing. The units list is a sample, so the server's dry run has the last word on
 * a model with more.
 */
function useOwnerBrands(
    modelId: ModelId,
    brandsById: Map<BrandId, Brand>,
): readonly (JoinBrand | undefined)[] | undefined {
    const [units, loadable] = useLoadableAtomValue(
        useMemo(() => equipmentForModelState(modelId), [modelId]),
        NO_UNITS,
    );
    const live = useMemo(() => units.filter((unit) => unit.status !== 'deleted'), [units]);
    const brands = useUnitBrands(live, brandsById);
    return useMemo(
        () => (loadable.state === 'hasData' ? live.map((unit) => brands.get(unit.id)?.join) : undefined),
        [loadable.state, live, brands],
    );
}

const NO_UNITS: Equipment[] = [];

const AFFINITY_TAGS: Record<TypeAffinity | 'none', ReactNode> = {
    type: (
        <HintTooltip hint="Filed under the same equipment type, so the type gate passes.">
            <Tag className="bg-emerald-200 text-emerald-900">same type</Tag>
        </HintTooltip>
    ),
    family: (
        <HintTooltip hint="Another type in the same type family. Units join across it, flagged for review.">
            <Tag className="bg-amber-200 text-amber-900">same family</Tag>
        </HintTooltip>
    ),
    none: (
        <HintTooltip hint="An unrelated type, so a different product under the same number. Units never join across it.">
            <Tag className="bg-rose-200 text-rose-900">other type</Tag>
        </HintTooltip>
    ),
};

const BRAND_TAGS: Record<BrandRelation, ReactNode> = {
    same: (
        <HintTooltip hint="The same catalog brand, or the same pending brand request.">
            <Tag className="bg-emerald-200 text-emerald-900">same brand</Tag>
        </HintTooltip>
    ),
    open: (
        <HintTooltip hint="One side has no brand, which is no evidence either way, so the brand gate passes.">
            <Tag>brand unset</Tag>
        </HintTooltip>
    ),
    contradicts: (
        <HintTooltip hint="Two different brands (or brand requests). The owner's brand is the truth, so these never join.">
            <Tag className="bg-rose-200 text-rose-900">other brand</Tag>
        </HintTooltip>
    ),
};

/** The two gates as tags: how a model (or unit) sits beside the model whose page this is. */
export const JoinFactTags: FC<{ facts: JoinFacts }> = ({ facts }) => (
    <>
        {AFFINITY_TAGS[facts.affinity ?? 'none']}
        {BRAND_TAGS[facts.brand]}
    </>
);

/** A tombstone's page: where its units went, and a way to finish a merge that died midway. */
export const MergedIntoCard: FC<{ model: Model; winnerId: ModelId; brandsById: Map<BrandId, Brand> }> = ({
    model,
    winnerId,
    brandsById,
}) => {
    const winner = useAsyncAtomValue(useMemo(() => modelState(winnerId), [winnerId]));
    const [rerunning, setRerunning] = useState(false);
    const closeRerun = useCallback((next: boolean) => {
        if (!next) setRerunning(false);
    }, []);
    return (
        <Card className="gap-0 py-4">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">Merged</CardTitle>
                <CardDescription>
                    Its units, spellings, known-as names and manual links moved to{' '}
                    <RouterLink className="underline" to={`/products/models/${encodeURIComponent(winnerId)}`}>
                        {winner?.modelDisplay ?? winnerId}
                    </RouterLink>
                    . Run the merge again to pick up anything a failed run left behind.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Button type="button" variant="outline" size="sm" onClick={() => setRerunning(true)}>
                    Run again
                </Button>
                {rerunning && (
                    <MergeModelDialog
                        open
                        onOpenChange={closeRerun}
                        loser={model}
                        winnerId={winnerId}
                        brandsById={brandsById}
                    />
                )}
            </CardContent>
        </Card>
    );
};
